import {
  convertBooleanToNumber,
  convertTimestampToDate,
  getExercisedData,
  getHourFromTimestamp,
  convertSmartOrderConfigType,
} from 'utils/securities/formatter';

import type {
  PortfolioState,
  PortfolioStateItem,
} from 'global/Securities/types/store/portfolio';
import type { TradingFormula } from 'global/Securities/types/store/formula';
import { OrderAction } from 'global/Securities/types/api/orderList';
import { OrderStateItem, OrderSide } from 'global/Securities/types/store/order';
import { BondPortfolioWithProduct } from 'global/Securities/types/api/bond';
import { STOCK_TYPE } from 'constants/trading';

export const convertSideToAction = (side: OrderSide): OrderAction =>
  side === OrderSide.Buy ? OrderAction.Buy : OrderAction.Sell;

const portfolioItem = (data: PortfolioItemTransform): PortfolioStateItem => {
  const {
    asset,
    company,
    exercise,
    flags,
    price,
    qty,
    symbol,
    type,
    info,
    bond,
    can_create_smart_order,
    is_sharia,
  } = data ?? {};

  return {
    allow_order: convertBooleanToNumber(flags?.allow_order),
    amount_invested: asset?.amount_invested,
    available_lot: qty?.available.lot,
    available_share: qty?.available.share,
    balance_lot: qty?.balance.lot,
    balance_share: qty?.balance.share,
    company_name: company?.name,
    exercise_data: getExercisedData(exercise),
    exercise_type: exercise?.type,
    have_smartorder: flags?.have_smart_order,
    price_average: price?.average.price,
    price_average_fee: price?.average.fee,
    price_latest: price?.latest,
    stock_on_hand: qty?.stock_on_hand,
    symbol,
    total: asset?.amount_invested,
    unrealised_gain: asset?.unrealised.gain,
    unrealised_marketvalue: asset?.unrealised.market_value,
    unrealised_profitloss: asset?.unrealised.profit_loss,
    type: type ?? '',
    info: info ?? [],
    bond: bond ?? undefined,
    show_create_smartorder: can_create_smart_order ?? false,
    can_buy: flags?.can_buy,
    can_sell: flags?.can_sell,
    is_sharia,
  };
};

const portfolioItemFromBonds = (
  data: BondPortfolioWithProduct,
): PortfolioStateItem => ({
  allow_order: convertBooleanToNumber(true), // allow buy more bonds
  amount_invested: data?.invested_amount,
  available_lot: parseInt(data?.total_units, 10),
  available_share: parseInt(data?.total_units, 10),
  balance_lot: parseInt(data?.total_units, 10),
  balance_share: parseInt(data?.total_units, 10),
  company_name: data.product.name,
  exercise_data: undefined,
  exercise_type: undefined,
  have_smartorder: false,
  price_average: data?.price_rate,
  price_average_fee: data?.price_rate,
  price_latest: undefined,
  stock_on_hand: data?.invested_amount,
  symbol: data?.product?.identifier,
  total: data?.total_portfolio,
  unrealised_gain: data?.profit_loss?.percentage / 100,
  unrealised_marketvalue: data?.market_value,
  unrealised_profitloss: data?.profit_loss?.amount,
  type: STOCK_TYPE.bond,
  info: undefined,
  bond: undefined,
  show_create_smartorder: false,
  bond_sell_enabled: parseInt(data?.units, 10) > 0,
  is_sharia: data?.product?.is_sharia,
  can_buy: false,
  can_sell: false,
});

const portfolioList = (portfolio: PortfolioListResponse): PortfolioState => {
  const {
    summary,
    smart_order_info,
    results: stockPortfolios,
    day_trade,
  } = portfolio ?? {};

  const result: Partial<PortfolioStateItem>[] = stockPortfolios.map(
    (stockPortfolio) => portfolioItem(stockPortfolio),
  );

  return {
    tradingbalance: summary?.trading.balance,
    amount_allocated: summary?.amount.allocated,
    amount_creditlimit: summary?.amount.credit_limit,
    unrealised_profitloss: summary?.profit_loss.unrealised,
    realised_profitloss: summary?.profit_loss.realised,
    show_create_smartorder: smart_order_info?.can_create,
    auto_order_info: smart_order_info?.info,
    day_trade: day_trade?.map((item) => portfolioItem(item)),
    debt_ratio: summary?.debt.ratio,
    debt_total: summary?.debt.total,
    debt_market_value: summary?.debt.market_value,
    amount_invested: summary?.amount.invested,
    gain: summary?.gain,
    profitloss: summary?.profit_loss.net,
    equity: summary?.equity,
    result,
  };
};

const orderDetail = (order: OrderItem): OrderStateItem => {
  const {
    action,
    amount,
    day_trade,
    gtc,
    market_order_id,
    message,
    order_id,
    price,
    qty,
    side,
    securities_kind,
    smart_order,
    status_text,
    symbol,
    time,
    is_sharia,
  } = order ?? {};

  const isBond = securities_kind === 'SECURITIES_KIND_BOND';

  return {
    action: convertSideToAction(side),
    amend_action: action?.can_amend,
    amount: amount?.amount,
    amount_fee: amount?.fee.amount,
    amount_invested: amount?.invested,
    amount_invested_fee: isBond ? undefined : amount?.fee.invested,
    amount_matched: isBond ? amount?.fee?.invested : amount?.matched,
    amount_matched_broker_fee: amount?.fee.matched.broker,
    amount_matched_exchange_fee: amount?.fee.matched.exchange,
    amount_matched_fee: amount?.fee.matched.amount,
    amount_matched_total: amount?.matched_total,
    amount_open: amount?.open,
    amount_open_fee: amount?.fee.open,
    cancel_action: action?.can_cancel,
    config_type: convertSmartOrderConfigType(smart_order?.config_type),
    gtc: convertBooleanToNumber(gtc?.is_gtc),
    label: smart_order?.label,
    market_orderid: market_order_id,
    message,
    order_done: isBond ? undefined : qty?.lot_done,
    order_open: qty?.lot_open,
    order_total: isBond ? undefined : qty?.lot_total,
    orderid: order_id,
    price: price?.order,
    price_average: price?.average.price,
    price_average_fee: price?.average.fee,
    price_average_portfolio: price?.average.portfolio,
    price_order: price?.order,
    smart_orderid: smart_order?.order_id ?? smart_order?.id,
    status: status_text,
    symbol,
    securities_kind,
    time: getHourFromTimestamp(time?.open ?? time?.opened_at),
    time_match: convertTimestampToDate(time?.match ?? time?.matched_at),
    time_open: convertTimestampToDate(time?.open ?? time?.opened_at),
    trigger_price: smart_order?.trigger_price,
    day_trade_is_day_trade: day_trade?.is_day_trade,
    day_trade_force_sell_event: day_trade?.force_sell_event,
    day_trade_message: day_trade?.message ?? '',
    is_sharia,
  };
};

const orderList = (orders: OrderItem[]): OrderStateItem[] => {
  const mappedData = orders?.map((order) => orderDetail(order));
  return mappedData;
};

const formulaData = (data: FormulaResponse): TradingFormula => {
  const { version, formula, fee, preview_fee, day_trade } = data ?? {};

  const dayTradePreviewFee = day_trade?.preview_fee
    ? day_trade.preview_fee
    : preview_fee;

  return {
    version,
    fee: {
      buy: fee.buy,
      sell: fee.sell,
    },
    formula: {
      buy_amount_broker_fee: formula.buy.broker_fee,
      buy_amount_exchange_fee: formula.buy.exchange_fee,
      buy_amount_fee: formula.buy.fee,
      buy_amount_invested: formula.buy.invested,
      buy_amount_invested_fee: formula.buy.invested_fee,

      ca_sell_amount_broker_fee: formula.ca_sell.broker_fee,
      ca_sell_amount_exchange_fee: formula.ca_sell.exchange_fee,
      ca_sell_amount_fee: formula.ca_sell.fee,
      ca_sell_amount_invested: formula.ca_sell.invested,
      ca_sell_amount_invested_fee: formula.ca_sell.invested_fee,

      sell_amount_broker_fee: formula.sell.broker_fee,
      sell_amount_exchange_fee: formula.sell.exchange_fee,
      sell_amount_fee: formula.sell.fee,
      sell_amount_invested: formula.sell.invested,
      sell_amount_invested_fee: formula.sell.invested_fee,

      sell_gain: formula.sell_gain,
      sell_profit_loss: formula.sell_profit_loss,

      amount_invested: formula.amount_invested,
      summary_equity: formula.summary.equity,
      summary_invested: formula.summary.invested,
      summary_net_profit_loss: formula.summary.net_profit_loss.value,
      summary_net_profit_loss_percentage:
        formula.summary.net_profit_loss.percentage,
      unrealised_gain: formula.unrealized.gain,
      unrealised_market_value: formula.unrealized.market_value,
      unrealised_profit_loss: formula.unrealized.profit_loss,

      day_trade_debt_ratio: formula.day_trade.debt.ratio,
      day_trade_debt_unrealized: formula.day_trade.debt.unrealized,
      day_trade_portion_order_amount: formula.day_trade.portion_order_amount,
      day_trade_investment_day_trade: formula.day_trade.investment.day_trade,
      day_trade_investment_portion_tb: formula.day_trade.investment.portion_tb,

      fee_buy: fee.buy,
      fee_sell: fee.sell,
      fee_buy_day_trade: day_trade.fee.buy,
      fee_sell_day_trade: day_trade.fee.sell,
    },
    preview_fee: {
      broker_fee: preview_fee.broker_fee,
      exchange: {
        compositions: preview_fee.exchange.compositions,
        title: preview_fee.exchange.title,
        desc: preview_fee.exchange.desc,
        subtitle: preview_fee.exchange.subtitle,
        exchange_fee_total_buy: preview_fee.exchange.total_buy,
        exchange_fee_total_ca_buy: preview_fee.exchange.total_ca_buy,
        exchange_fee_total_ca_sell: preview_fee.exchange.total_ca_sell,
        exchange_fee_total_sell: preview_fee.exchange.total_sell,
        exchange_fee_total_title: preview_fee.exchange.total_title,
      },
    },
    day_trade: {
      fee: {
        buy: day_trade.fee.buy,
        sell: day_trade.fee.sell,
      },
      portion_deduct_to_tb: day_trade.portion_deduct_to_tb,
      preview_fee: {
        broker_fee: dayTradePreviewFee.broker_fee,
        exchange: {
          compositions: dayTradePreviewFee.exchange.compositions,
          title: dayTradePreviewFee.exchange.title,
          desc: dayTradePreviewFee.exchange.desc,
          subtitle: dayTradePreviewFee.exchange.subtitle,
          exchange_fee_total_buy: dayTradePreviewFee.exchange.total_buy,
          exchange_fee_total_ca_buy: dayTradePreviewFee.exchange.total_ca_buy,
          exchange_fee_total_ca_sell: dayTradePreviewFee.exchange.total_ca_sell,
          exchange_fee_total_sell: dayTradePreviewFee.exchange.total_sell,
          exchange_fee_total_title: dayTradePreviewFee.exchange.total_title,
        },
      },
    },
  };
};

export default {
  portfolioItem,
  portfolioList,
  orderDetail,
  orderList,
  formulaData,
  portfolioItemFromBonds,
};
